import type { FC, HTMLAttributes } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { FormattedDate, useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { BlockedContent } from '../../../../../../graphdebate/src/components/Blocking/BlockedContent';
import { ReportedContentBanner } from '../../../../../../graphdebate/src/components/Report/ReportedContentBanner';
import { Tooltip } from '../../../../components/GraphModal/Tooltip';
import { ModerationUserVisibilityOnlyInfos } from '../../../../components/Moderation/ModerationInfos';
import { dsaClassHandler } from '../../../../contributor/Profile/DSA/dsaClassHandler';
import { DsaMessage } from '../../../../contributor/Profile/DSA/DsaMessage';
import { ProfilePopover } from '../../../../contributor/Profile/ProfilePopover';
import { useConnectedRouter } from '../../../../hooks/useConnectedRouter';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { useWebsite } from '../../../../hooks/useWebsite';
import { blockedMessages } from '../../../../i18n/blocked';
import type { Topic, User, Vote } from '../../../../types/api';
import { getLocaleDateDigits } from '../../../../utils/date';
import type { EjsHighlightedEmbed } from '../../../../utils/editor';
import { getCardViewContent, getEjsHighlightedEmbed } from '../../../../utils/editor';
import { parseJSONString } from '../../../../utils/json';
import { AuthorAvatar } from '../../../AuthorAvatar';
import { EmbedsOverview } from '../../../Content/EmbedsOverview';
import { getAuthorPseudo } from '../../../thread-utils';
import type { MessageParsedEmbeds } from '../../../ThreadMessage/threadTypes';
import { HighlightedEmbed } from '../HighlightedEmbed';
import { TopicContent } from '../v3/TopicContent';
import { TopicItemInteractions } from '../v3/TopicItemInteractions';
import TopicItemTitle from '../v3/TopicItemTitle';
import './CardStyles.scss';
import { CardViewTopicBottomActions } from './CardViewTopicBottomActions';

interface TopicListItemCardProps extends HTMLAttributes<HTMLDivElement> {
  topic: Topic;
  withRemove?: boolean;
  withActions?: boolean;
  isResponsive?: boolean;
  isSidebar?: boolean;
  itemTitleClassName?: string;
  shouldHandleDsaDisplay?: boolean;
  isAuthorBlocked?: boolean;
}

const getProperHeightLimiter = (
  isMobileDisplay: boolean,
  highlightedType?: EjsHighlightedEmbed['type'],
) => {
  switch (true) {
    case ['image', 'embed'].some(t => highlightedType === t):
      return isMobileDisplay ? 120 : 170;
    case ['linkTool', 'googleForm'].some(t => highlightedType === t):
      return isMobileDisplay ? 300 : 350;
    default:
      return isMobileDisplay ? 400 : 450;
  }
};

export const TopicListItemCard: FC<TopicListItemCardProps> = memo(function TopicListItemCard(
  props,
) {
  const {
    topic,
    withRemove,
    withActions,
    className,
    isResponsive = false,
    isSidebar = false,
    itemTitleClassName = '',
    shouldHandleDsaDisplay = false,
    isAuthorBlocked = false,
    ...otherProps
  } = props;
  const { graphdebate } = useWebsite();
  const { push } = useConnectedRouter();
  // @ts-ignore
  const shouldIndexDiscussion = graphdebate?.meta?.topic?.index ?? 0;

  const [currentVotes, setCurrentVotes] = useState<Vote[]>((topic.votes as Vote[]) || []);

  const { locale, formatMessage } = useIntl();

  const displayedDate = getLocaleDateDigits(topic.createdAt, locale);

  const isMobileDisplay = useIsMobile();

  const { parsedEmbeds, cardViewEmbeds, cardViewHighlightedEmbeds } = useMemo(() => {
    let embeds: Partial<{
      parsedEmbeds: MessageParsedEmbeds | null;
      cardViewEmbeds: string;
      cardViewHighlightedEmbeds: EjsHighlightedEmbed | null;
    }> = {};

    const parsed = parseJSONString<MessageParsedEmbeds>(topic.embeds);
    embeds.parsedEmbeds = parsed;

    if (parsed) {
      const isOnlyOneBlock = parsed.blocks.length === 1;

      embeds.cardViewEmbeds = JSON.stringify(getCardViewContent(parsed));

      embeds.cardViewHighlightedEmbeds = !isOnlyOneBlock ? getEjsHighlightedEmbed(parsed, 1) : null;
    }
    return embeds;
  }, [topic.embeds]);

  const isOnlyOneBlock = parsedEmbeds?.blocks.length === 1;
  const shouldRemoveLimiter = isOnlyOneBlock && parsedEmbeds?.blocks[0].type !== 'paragraph';

  const isFullWidthContent =
    isOnlyOneBlock && ['embed', 'image'].includes(parsedEmbeds?.blocks[0].type);

  const handleRedirectToUnlimitedTopicPage = useCallback(() => {
    push({ pathname: `/t/${topic.slug}`, query: { cardRedirect: true } });
  }, [topic.slug, push]);

  const displayedPseudo = useMemo(
    () =>
      isAuthorBlocked
        ? formatMessage(blockedMessages.blockedUserLabel)
        : getAuthorPseudo(topic.author),
    [isAuthorBlocked, topic.author, formatMessage],
  );

  const displayedProfileContent = useMemo(() => {
    return (
      <div className="flex items-center gap-x-1 md:gap-x-2">
        <AuthorAvatar
          author={topic.author}
          userId={(topic.author as User).id}
          profile={(topic.author as User).profile}
          size="medium"
          className={isResponsive ? 'md:w-10 md:h-10' : ''}
          isUserBlocked={isAuthorBlocked}
        />
        <div
          className={twMerge(
            'author font-medium truncate cursor-pointer hover:text-primary',
            isResponsive && 'md:font-semibold',
          )}
        >{`${displayedPseudo}, `}</div>
      </div>
    );
  }, [topic.author, isResponsive, isAuthorBlocked, displayedPseudo]);

  const actualTopicContent = useMemo(
    () => (
      <TopicContent
        heightLimit={getProperHeightLimiter(isMobileDisplay, cardViewHighlightedEmbeds?.type)}
        content={topic.content as string}
        embeds={cardViewEmbeds}
        status={topic.status as string}
        shouldHandleDsaDisplay={shouldHandleDsaDisplay}
        className={twMerge('m-0', isFullWidthContent && 'wideMedia')}
        onLimiterClick={handleRedirectToUnlimitedTopicPage}
        noLimiter={shouldRemoveLimiter}
      />
    ),

    [
      isMobileDisplay,
      cardViewHighlightedEmbeds?.type,
      topic.content,
      topic.status,
      cardViewEmbeds,
      shouldHandleDsaDisplay,
      isFullWidthContent,
      handleRedirectToUnlimitedTopicPage,
      shouldRemoveLimiter,
    ],
  );

  const contentToDisplay = useMemo(() => {
    if (topic.userReport) {
      return (
        <ReportedContentBanner createdAt={topic.userReport.createdAt} className="mt-3 mx-3">
          {actualTopicContent}
        </ReportedContentBanner>
      );
    }
    return actualTopicContent;
  }, [topic.userReport, actualTopicContent]);

  return (
    <div
      className={twMerge(
        'TopicListItemCard flex flex-col text-gray-darkv4',
        shouldHandleDsaDisplay && [dsaClassHandler(topic.status), 'my-4'],
        className,
      )}
      {...otherProps}
    >
      <div className="topicContent flex flex-col gap-y-3">
        <div className="infos flex-1 overflow-hidden">
          <div className="head flex">
            <div
              className={twMerge(
                'post-infos flex items-center gap-x-1 text-[15px]',
                isSidebar && 'md:text-xs',
                isResponsive && 'md:text-[15px]',
              )}
            >
              {!isAuthorBlocked ? (
                <ProfilePopover
                  userId={(topic.author as User).id}
                  trigger={isMobileDisplay ? 'click' : 'mouseenter'}
                  delay={[500, 0]}
                >
                  {displayedProfileContent}
                </ProfilePopover>
              ) : (
                displayedProfileContent
              )}

              <div
                className={twMerge(
                  'date text-gray-soft',
                  isSidebar && 'md:text-xs md:text-gray-400 md:font-medium',
                )}
              >
                <Tooltip
                  content={
                    <FormattedDate
                      value={new Date(topic.createdAt)}
                      year="numeric"
                      month="long"
                      day="numeric"
                      weekday="long"
                      hour="numeric"
                      minute="numeric"
                    />
                  }
                  touch={true}
                  touchTimeout={1500}
                >
                  <span>{displayedDate}</span>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Native blocking: replace topic content with blocked notice */}
          {isAuthorBlocked ? (
            <BlockedContent className="mt-3 mx-3" />
          ) : (
            <>
              <TopicItemTitle
                topic={topic}
                isResponsive={isResponsive}
                itemTitleClassName={'text-[20px] mt-[5px] mb-[10px]'}
                className="font-medium"
                addLinkParams={{ cardRedirect: 'true' }}
              />

              {contentToDisplay}
            </>
          )}
        </div>
        {!isAuthorBlocked && cardViewHighlightedEmbeds && (
          <HighlightedEmbed embedToHighLight={cardViewHighlightedEmbeds} />
        )}

        {!isAuthorBlocked && parsedEmbeds && (
          <EmbedsOverview messageEmbeds={parsedEmbeds} className="my-0" avoidSoleEmbedOverview />
        )}

        {!isAuthorBlocked && (
          <TopicItemInteractions
            topic={topic}
            isResponsive={false}
            isSidebar={isSidebar}
            currentVotes={currentVotes}
          />
        )}
      </div>

      {!isAuthorBlocked && (
        <CardViewTopicBottomActions
          topic={topic}
          onVoteUpdate={setCurrentVotes}
          isAuthorBlocked={isAuthorBlocked}
        />
      )}

      <DsaMessage
        contentType="topic"
        contentStatus={topic.status}
        moderationMotifId={topic.moderationMotifId}
        className={twMerge(
          'absolute top-0 right-4 transform -translate-y-1/3 px-3 rounded-full text-white text-sm',
          topic.status === 'pending' ? 'bg-gray-light' : 'bg-primary',
        )}
      />

      {shouldHandleDsaDisplay && topic.status !== 'approved' && (
        <ModerationUserVisibilityOnlyInfos
          contentType="message"
          className="absolute bottom-0 right-2 text-gray-medium text-sm"
        />
      )}
    </div>
  );
});
